import React from 'react';
import {
  Box,
  Alert,
  Typography
} from '@mui/material';
import type { ValidationRules } from '../../../../types/accounting';
import { JournalEntry } from '../../../../types/journalEntry';
import { RestrictionType } from '../../../../types/accounting';

interface ValidationResults {
  errors: string[];
  warnings: string[];
  info: string[];
}

interface JournalEntryValidationProps {
  validationResults: ValidationResults;
  formErrors: string[];
  onClearErrors: () => void;
}

export const JournalEntryValidation: React.FC<JournalEntryValidationProps> = ({
  validationResults,
  formErrors,
  onClearErrors
}) => {
  if (!validationResults.errors.length && 
      !validationResults.warnings.length && 
      !formErrors.length) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {validationResults.errors.map((error, i) => (
        <Alert key={`validation-error-${i}`} severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ))}
      
      {validationResults.warnings.map((warning, i) => (
        <Alert key={`validation-warning-${i}`} severity="warning" sx={{ mb: 1 }}>
          {warning}
        </Alert>
      ))}

      {formErrors.length > 0 && (
        <Alert 
          severity="error" 
          sx={{ mb: 1 }}
          onClose={onClearErrors}
        >
          {formErrors.map((errorMessage, index) => (
            <Typography 
              key={`form-error-${index}`} 
              variant="body2" 
              color="error"
              component="div"
            >
              {errorMessage}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
};

interface ValidationRule {
  condition: (entry: JournalEntry) => boolean;
  message: string;
  severity: 'error' | 'warning' | 'info';
  category: 'accounting' | 'fiscal' | 'compliance';
}

export const VALIDATION_RULES: ValidationRule[] = [
  {
    condition: (entry: JournalEntry) => entry.debit === entry.credit,
    message: "Debits must equal credits",
    severity: "error",
    category: "accounting"
  },
  {
    condition: (entry: JournalEntry) => {
      const isRestricted = entry.fiscalCategory?.restrictionType === RestrictionType.TEMPORARILY_RESTRICTED || 
                          entry.fiscalCategory?.restrictionType === RestrictionType.PERMANENTLY_RESTRICTED;
      const hasValidFund = !!entry.fiscalCategory?.fundId;
      return !isRestricted || (isRestricted && hasValidFund);
    },
    message: "Restricted funds require fund allocation",
    severity: "error",
    category: "fiscal"
  }
  // Add more rules as needed
]; 